import { DEFAULT_PLACEHOLDER_GENDER } from 'constants/profiles'
import { DateTime } from 'luxon'
import { checkUrl } from './urls'

import { FALL, SPRING } from 'types/calendar.types'

import { Student } from 'types/student.types'
import { PLACEHOLDER_PHOTO_PATH } from 'config/app.config'
import { getDraftBaseUrl } from 'utils/draftBase'

export const getClassLevel = (year: number): string => {
  const currentDate = DateTime.now() // Get the current date using luxon
  const currentYear = currentDate.year // Extract the year
  const currentSemester = currentDate.month >= 8 ? FALL : SPRING
  return getClassLevelFromCurrentYear(year, currentYear, currentSemester)
}

// Separate function to get the class level (without using Luxon, for simpler unit testing)
export const getClassLevelFromCurrentYear = (
  year: number,
  currentYear: number,
  semester: string
): string => {
  switch (semester) {
    case FALL:
      switch (year - currentYear) {
        case 1:
          return 'senior'
        case 2:
          return 'junior'
        case 3:
          return 'sophomore'
        case 4:
          return 'first year'
        default:
          return 'grad'
      }

    default:
      switch (year - currentYear) {
        case 0:
          return 'senior'
        case 1:
          return 'junior'
        case 2:
          return 'sophomore'
        case 3:
          return 'first year'
        default:
          return 'grad'
      }
  }
}

export const isGradStudent = (classOf: number): boolean => {
  const currentDate = DateTime.now()
  const currentYear = currentDate.year
  const currentMonth = currentDate.month
  
  // Aug-Dec (months 8-12): true if classOf is equal to current year or less
  if (currentMonth >= 8 && currentMonth <= 12) {
    return classOf <= currentYear
  }
  
  // Any other month (Jan-Jul): true if classOf is less than current year
  return classOf < currentYear
}

export const getClassLevelDisplayString = (tutor: Student): string => {
  return `🎉&nbsp; ${getClassLevel(tutor.classOf)} &nbsp;🎉`
}

export const getProfilePhotoPath = (tutor: Student): string => {
  const { firstName, lastName, classOf } = tutor

  const firstNameCleaned = firstName.replace(/[^a-zA-Z0-9]/g, '_')
  const lastNameCleaned = lastName.replace(/[^a-zA-Z0-9]/g, '_')

  const gradYear = isGradStudent(classOf) ? 'grad' : classOf

  return `images/students/${gradYear}/${firstNameCleaned.toLowerCase()}_${lastNameCleaned.toLowerCase()}.jpg`
}

export const getDefaultPlaceholderPhotoPath = async (): Promise<string> => {
  return `${await getDraftBaseUrl()}/${PLACEHOLDER_PHOTO_PATH}`
}

export const getStudentPhotoPath = async (student: Student): Promise<string> => {
  const profilePhotoPath = getProfilePhotoPath(student)
  const imageURL = `${await getDraftBaseUrl()}/${profilePhotoPath}`
  return imageURL
}

/**
 * Check all image URLs for a list of Students in parallel
 * @param students - Array of Students
 * @returns boolean[] - Array of booleans indicating if the image URL is valid
 */
export async function checkAllImageUrls(
  students: Student[]
): Promise<{ imageUrl: string; isValid: boolean }[]> {
  const checks = students.map(async student => {
    const profilePhotoPath = getProfilePhotoPath(student)
    const imageUrl = `${await getDraftBaseUrl()}/${profilePhotoPath}`
    const isValid = await checkUrl(imageUrl)
    return {
      imageUrl,
      isValid,
    }
  })

  return Promise.all(checks)
}
